import {useEffect, useRef, useState} from 'react';

import {classNames} from '../../../util/css';
import {getImageFromUrl} from '../../../util/image';

type PropsType = {
    baseImageSrc: string;
    logoUrl: string | undefined;
    className?: string;
    /** Logo position as fraction of image size (0–1). Default: top-left area on the "screen" */
    logoPosition?: {left: number; top: number};
    /** Logo width as fraction of image width (0–1). Height keeps logo aspect ratio. */
    logoWidthFraction?: number;
};

export function LandingImageWithLogo(props: PropsType): JSX.Element {
    const {baseImageSrc, logoUrl, className, logoPosition = {left: 0.17, top: 0.25}, logoWidthFraction = 0.13} = props;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [fallbackToImg, setFallbackToImg] = useState(false);

    useEffect(() => {
        let cancelled = false;

        if (logoUrl) {
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');

            if (canvas && context) {
                Promise.all([getImageFromUrl(baseImageSrc), getImageFromUrl(logoUrl)])
                    .then(([baseImg, logoImg]) => {
                        if (cancelled) {
                            return;
                        }

                        const imageWidth = baseImg.naturalWidth;
                        const imageHeight = baseImg.naturalHeight;

                        canvas.width = imageWidth;
                        canvas.height = imageHeight;
                        context.drawImage(baseImg, 0, 0);

                        const logoWidth = imageWidth * logoWidthFraction;
                        const logoHeight = logoWidth * (logoImg.naturalHeight / logoImg.naturalWidth);

                        context.drawImage(
                            logoImg,
                            imageWidth * logoPosition.left,
                            imageHeight * logoPosition.top,
                            logoWidth,
                            logoHeight
                        );
                    })
                    .catch(() => {
                        if (!cancelled) {
                            setFallbackToImg(true);
                        }
                    });
            } else {
                setFallbackToImg(true);
            }
        } else {
            setFallbackToImg(true);
        }

        return () => {
            cancelled = true;
        };
    }, [baseImageSrc, logoUrl, logoPosition.left, logoPosition.top, logoWidthFraction]);

    if (fallbackToImg || !logoUrl) {
        return <img alt="" className={classNames(className)} src={baseImageSrc} />;
    }

    return <canvas className={classNames(className)} ref={canvasRef} />;
}
